import { v4 as uuidv4 } from "uuid";
import { getNetworkData } from "./networkInfo";
import PerformanceMonitoringService from "./services/performanceMonitoringService";

export type TrackingStatus = "running" | "completed" | "failed" | "cancelled";

export interface TimingInfo {
  startTime: number;
  endTime?: number;
  duration?: number;
  startDate: Date;
  endDate?: Date;
}

export interface TrackedOperation {
  id: string;
  name: string;
  status: TrackingStatus;
  timing: TimingInfo;
  parentId?: string;
  children: string[];
  metadata?: Record<string, unknown>;
  error?: Error | string;
  interactionName: string;
}

export interface TrackedInteraction {
  id: string;
  name: string;
  status: TrackingStatus;
  timing: TimingInfo;
  operations: string[];
  metadata?: Record<string, unknown>;
  error?: Error | string;
}

export interface InteractionSummary {
  name: string;
  status: TrackingStatus;
  duration: number;
  operationCount: number;
  successOperationCount: number;
  failedOperationCount: number;
  slowestOperation?: { name: string; duration: number };
  operations: InteractionSummary[];
  metadata?: Record<string, unknown>;
}

export interface InteractionResult {
  interaction: TrackedInteraction;
  summary: InteractionSummary;
}

export enum TracingMode {
  OFF = "OFF",
  FAILED = "FAILED",
  SAMPLING = "SAMPLING",
  ALL = "ALL",
}

export interface BatchPayload {
  metrics: ({
    name: string;
    effectiveNetworkType?: string;
    networkStrength: string;
    browser: string;
    os: string;
  } & InteractionSummary)[];
  sessionId?: string;
  timestamp: string;
  userId?: string;
  locationId?: string;
  routeName?: string;
}

export interface TrackerConfig {
  mode: TracingMode;
  samplingRate: number;
  enableLogging?: boolean;
  onComplete?: (name: string, result: InteractionResult) => void;
  /** Debounce delay in ms before pushing batch */
  batchDelayMs: number;
  batchPusher: (payload: BatchPayload) => void;
}

const generateId = (): string => {
  try {
    return uuidv4();
  } catch {
    // Fallback to a simple random ID if uuid fails
    return `${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
  }
};

const now = (): number => {
  try {
    return typeof performance !== "undefined" &&
      typeof performance.now === "function"
      ? performance.now()
      : Date.now();
  } catch {
    return Date.now();
  }
};

const createTiming = (): TimingInfo => {
  try {
    return { startTime: now(), startDate: new Date() };
  } catch {
    return { startTime: Date.now(), startDate: new Date() };
  }
};

const completeTiming = (timing: TimingInfo): TimingInfo => {
  try {
    const endTime = now();
    const startTime = timing?.startTime ?? endTime;
    return {
      ...timing,
      endTime,
      endDate: new Date(),
      duration: Math.max(0, endTime - startTime),
    };
  } catch {
    const endTime = Date.now();
    return {
      ...timing,
      endTime,
      endDate: new Date(),
      duration: 0,
    };
  }
};

const getSessionId = (): string | undefined => {
  try {
    return sessionStorage?.getItem?.("sessionId") || undefined;
  } catch {
    return undefined;
  }
};

const safeGetNetworkData = () => {
  try {
    return getNetworkData();
  } catch {
    return {
      effectiveType: undefined,
      strength: "unknown",
      browser: "unknown",
      os: "unknown",
    };
  }
};

export class InteractionTracker {
  private config: TrackerConfig = {
    mode: TracingMode.ALL,
    samplingRate: 1,
    enableLogging: true,
    batchDelayMs: 2000,
    batchPusher: this.defaultBatchPusher,
  };
  private interactions: Map<string, TrackedInteraction> = new Map();
  private operations: Map<string, TrackedOperation> = new Map();
  private history: InteractionResult[] = [];
  private metricsBatch: ({
    traceId: string;
    name: string;
    effectiveNetworkType?: string;
    networkStrength: string;
    browser: string;
    os: string;
    context: string;
  } & InteractionSummary &
    Record<string, unknown>)[] = [];
  private globalMetadata: Record<string, unknown> = {};
  private batchTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(config: Partial<TrackerConfig> = {}) {
    try {
      this.config = { ...this.config, ...config };
    } catch {
      // Use default config if merge fails
    }
  }

  configure(config: Partial<TrackerConfig>): void {
    try {
      this.config = { ...this.config, ...config };
    } catch {
      // Silently ignore configuration errors
    }
  }

  /**
   * Set global metadata that will be attached to all interactions
   * @example
   * tracker.setGlobalMetadata({ userId: '123', locationId: 'loc-456' })
   */
  setGlobalMetadata(metadata: Record<string, unknown>): void {
    try {
      if (metadata && typeof metadata === "object") {
        this.globalMetadata = { ...this.globalMetadata, ...metadata };
      }
    } catch {
      // Silently ignore metadata errors
    }
  }

  /**
   * Get current global metadata
   */
  getGlobalMetadata(): Record<string, unknown> {
    try {
      return { ...this.globalMetadata };
    } catch {
      return {};
    }
  }

  /**
   * Clear all global metadata
   */
  clearGlobalMetadata(): void {
    try {
      this.globalMetadata = {};
    } catch {
      // Silently ignore
    }
  }

  /**
   * Remove specific keys from global metadata
   * @example
   * tracker.removeGlobalMetadata(['userId', 'locationId'])
   */
  removeGlobalMetadata(keys: string[]): void {
    try {
      if (Array.isArray(keys)) {
        keys.forEach((key) => {
          if (typeof key === "string") {
            delete this.globalMetadata[key];
          }
        });
      }
    } catch {
      // Silently ignore
    }
  }

  /**
   * Start a new interaction
   * @param name Unique name
   * @param options Optional metadata and settings
   */
  startInteraction(
    name: string,
    options?: { metadata?: Record<string, unknown> }
  ): string {
    try {
      if (!name || typeof name !== "string") {
        this.logWarn("Invalid interaction name provided");
        return "";
      }

      // Check if already exists
      if (this.interactions.has(name)) {
        this.log(`Interaction "${name}" already active, ending previous`);
        this.endInteraction(name);
      }

      const interaction: TrackedInteraction = {
        id: generateId(),
        name,
        status: "running",
        timing: createTiming(),
        operations: [],
        metadata: options?.metadata,
      };

      this.interactions.set(name, interaction);
      this.log(`[${name}] Started`);

      return name;
    } catch (error) {
      this.logWarn(`Failed to start interaction "${name}": ${error}`);
      return name || "";
    }
  }

  /**
   * End an interaction
   */
  endInteraction(
    name: string,
    error?: Error | string
  ): InteractionResult | null {
    try {
      if (!name || typeof name !== "string") {
        return null;
      }

      const interaction = this.interactions.get(name);
      if (!interaction) {
        this.logWarn(`Interaction "${name}" not found`);
        return null;
      }

      // Complete timing
      interaction.timing = completeTiming(interaction.timing);
      interaction.status = error ? "failed" : "completed";
      if (error) interaction.error = error;

      // End any pending operations
      this.endPendingOperations(interaction.operations);

      // Calculate summary
      const summary = this.calculateSummary(interaction);
      const result: InteractionResult = { interaction, summary };

      this.batchMetrics(summary);

      this.log(
        `[${name}] Completed in ${summary.duration.toFixed(0)}ms`,
        result
      );

      // Cleanup
      this.cleanupInteraction(name, interaction);

      // Callback (fail-safe)
      try {
        this.config.onComplete?.(name, result);
      } catch {
        // Silently ignore callback errors
      }

      return result;
    } catch (err) {
      this.logWarn(`Failed to end interaction "${name}": ${err}`);
      // Attempt cleanup even if there was an error
      try {
        this.interactions.delete(name);
      } catch {
        // Silently ignore
      }
      return null;
    }
  }

  /**
   * Cancel an interaction
   */
  cancelInteraction(name: string): void {
    try {
      if (!name || typeof name !== "string") {
        return;
      }

      const interaction = this.interactions.get(name);
      if (!interaction) return;

      interaction.timing = completeTiming(interaction.timing);
      interaction.status = "cancelled";
      this.cancelOperations(interaction.operations);
      this.cleanupInteraction(name, interaction);
      this.log(`[${name}] Cancelled`);
    } catch (error) {
      this.logWarn(`Failed to cancel interaction "${name}": ${error}`);
      // Attempt cleanup
      try {
        this.interactions.delete(name);
      } catch {
        // Silently ignore
      }
    }
  }

  /**
   * Check if interaction is active
   */
  isActive(name: string): boolean {
    try {
      return name ? this.interactions.has(name) : false;
    } catch {
      return false;
    }
  }

  /**
   * Get all active interaction names
   */
  getActiveInteractions(): string[] {
    try {
      return Array.from(this.interactions.keys());
    } catch {
      return [];
    }
  }

  getInteraction(name: string): TrackedInteraction | undefined {
    try {
      return name ? this.interactions.get(name) : undefined;
    } catch {
      return undefined;
    }
  }

  /**
   * Track an async interaction
   * Automatically starts and ends the interaction around the async function
   *
   * @example
   * ```typescript
   * const result = await tracker.trackInteraction('load-dashboard', async () => {
   *   const user = await fetchUser()
   *   const data = await fetchData()
   *   return { user, data }
   * })
   * ```
   */
  async trackInteraction<T>(
    name: string,
    fn: () => Promise<T>,
    options?: { metadata?: Record<string, unknown> }
  ): Promise<T> {
    this.startInteraction(name, options);
    try {
      const result = await fn();
      this.endInteraction(name);
      return result;
    } catch (error) {
      this.endInteraction(name, error as Error);
      throw error;
    }
  }

  /**
   * Track a sync interaction
   * Automatically starts and ends the interaction around the sync function
   *
   * @example
   * ```typescript
   * const result = tracker.trackInteractionSync('process-data', () => {
   *   return processData(rawData)
   * })
   * ```
   */
  trackInteractionSync<T>(
    name: string,
    fn: () => T,
    options?: { metadata?: Record<string, unknown> }
  ): T {
    this.startInteraction(name, options);
    try {
      const result = fn();
      this.endInteraction(name);
      return result;
    } catch (error) {
      this.endInteraction(name, error as Error);
      throw error;
    }
  }

  /**
   * Start an operation within an interaction
   * Returns empty string if interaction is not active (fail-safe)
   */
  startOperation(
    interactionName: string,
    operationName: string,
    options?: {
      parentOperation?: string;
      metadata?: Record<string, unknown>;
    }
  ): string {
    try {
      if (!interactionName || !operationName) {
        this.logWarn("Invalid interaction or operation name provided");
        return "";
      }

      const interaction = this.interactions.get(interactionName);
      if (!interaction) {
        this.logWarn(
          `Interaction "${interactionName}" is not active, skipping operation "${operationName}"`
        );
        return "";
      }

      let parentKey: string | undefined;
      if (options?.parentOperation) {
        parentKey = this.getOperationKey(
          interactionName,
          options.parentOperation
        );
      }

      const opId = generateId();
      const operation: TrackedOperation = {
        id: opId,
        name: operationName,
        status: "running",
        timing: createTiming(),
        parentId: parentKey,
        children: [],
        metadata: options?.metadata,
        interactionName,
      };

      const opKey = this.getOperationKey(interactionName, operationName);
      const existingOperation = this.operations.get(opKey);
      if (existingOperation) {
        this.logWarn(`Operation "${opKey}" already exists`);
        return opKey;
      }

      this.operations.set(opKey, operation);

      // Add to parent operation or interaction
      if (parentKey) {
        const parent = this.operations.get(parentKey);
        if (parent) {
          parent.children.push(opKey);
        } else {
          interaction.operations.push(opKey);
        }
      } else {
        interaction.operations.push(opKey);
      }

      this.log(`[${opKey}] Started`);
      return opKey;
    } catch (error) {
      this.logWarn(`Failed to start operation "${operationName}": ${error}`);
      return "";
    }
  }

  /**
   * End an operation
   */
  endOperation(
    interactionName: string,
    operationName: string,
    error?: Error | string
  ): void {
    try {
      if (!interactionName || !operationName) {
        return;
      }

      const opKey = this.getOperationKey(interactionName, operationName);
      const operation = this.operations.get(opKey);
      if (!operation) {
        this.logWarn(`Operation "${opKey}" not found`);
        return;
      }

      operation.timing = completeTiming(operation.timing);
      operation.status = error ? "failed" : "completed";
      if (error) operation.error = error;

      const duration = operation.timing.duration ?? 0;
      this.log(`[${opKey}] ${duration.toFixed(0)}ms`);
    } catch (err) {
      this.logWarn(`Failed to end operation "${operationName}": ${err}`);
    }
  }

  /**
   * Track an async operation
   * Executes the function even if tracking fails
   */
  async trackOperation<T>(
    interactionName: string,
    operationName: string,
    fn: () => Promise<T>,
    options?: { parentOperation?: string; metadata?: Record<string, unknown> }
  ): Promise<T> {
    const opStarted = this.startOperation(
      interactionName,
      operationName,
      options
    );
    try {
      const result = await fn();
      if (opStarted) {
        this.endOperation(interactionName, operationName);
      }
      return result;
    } catch (error) {
      if (opStarted) {
        this.endOperation(interactionName, operationName, error as Error);
      }
      throw error;
    }
  }

  /**
   * Track a sync operation
   * Executes the function even if tracking fails
   */
  trackOperationSync<T>(
    interactionName: string,
    operationName: string,
    fn: () => T,
    options?: { parentOperation?: string; metadata?: Record<string, unknown> }
  ): T {
    const opStarted = this.startOperation(
      interactionName,
      operationName,
      options
    );
    try {
      const result = fn();
      if (opStarted) {
        this.endOperation(interactionName, operationName);
      }
      return result;
    } catch (error) {
      if (opStarted) {
        this.endOperation(interactionName, operationName, error as Error);
      }
      throw error;
    }
  }

  getHistory(): InteractionResult[] {
    try {
      return [...this.history];
    } catch {
      return [];
    }
  }

  clearHistory(): void {
    try {
      this.history = [];
    } catch {
      // Silently ignore
    }
  }

  private batchMetrics(summary: InteractionSummary): void {
    try {
      if (this.shouldTrace(summary.status)) {
        const network = safeGetNetworkData();
        const { effectiveType, strength, browser, os } = network;

        const { operations, name } = summary;
        const allOps = this.flattenOperations(operations);

        const traceId = generateId();
        this.metricsBatch.push({
          effectiveNetworkType: effectiveType,
          networkStrength: strength,
          browser,
          os,
          traceId,
          context: name,
          ...summary,
          ...this.globalMetadata,
        });

        allOps.forEach((op) => {
          try {
            this.metricsBatch.push({
              effectiveNetworkType: effectiveType,
              networkStrength: strength,
              browser,
              os,
              traceId,
              context: name,
              ...op,
              ...this.globalMetadata,
            });
          } catch {
            // Silently ignore individual operation batch errors
          }
        });
      }

      // Safe timer handling
      try {
        if (this.batchTimer) {
          clearTimeout(this.batchTimer);
        }
        this.batchTimer = setTimeout(
          this.flushBatch,
          this.config.batchDelayMs ?? 2000
        );
      } catch {
        // Silently ignore timer errors
      }
    } catch (error) {
      this.logWarn(`Failed to batch metrics: ${error}`);
    }
  }

  private shouldTrace(status: TrackingStatus): boolean {
    try {
      switch (this.config.mode) {
        case "OFF":
          return false;
        case TracingMode.FAILED:
          return status === "failed";
        case TracingMode.SAMPLING:
          return (
            status === "failed" ||
            Math.random() < (this.config.samplingRate ?? 1)
          );
        case "ALL":
          return true;
        default:
          return false;
      }
    } catch {
      return false;
    }
  }

  private flushBatch = () => {
    try {
      if (!this.metricsBatch || this.metricsBatch.length === 0) return;

      const payload: BatchPayload = {
        metrics: this.metricsBatch,
        sessionId: getSessionId(),
        timestamp: new Date().toISOString(),
      };

      // Clear batch before pushing (in case push is async)
      this.metricsBatch = [];

      if (typeof this.config.batchPusher === "function") {
        try {
          this.config.batchPusher(payload);
        } catch {
          // Silently ignore batch pusher errors
        }
      }
    } catch {
      // Silently ignore flush errors
      this.metricsBatch = [];
    }
  };

  private getOperationKey(
    interactionName: string,
    operationName: string
  ): string {
    try {
      return `${interactionName ?? ""}/${operationName ?? ""}`;
    } catch {
      return "";
    }
  }

  private endPendingOperations(operations: string[]): void {
    try {
      if (!Array.isArray(operations)) return;

      for (const op of operations) {
        try {
          const operation = this.operations.get(op);
          if (!operation) {
            this.logWarn(`Operation "${op}" not found`);
            continue; // Use continue instead of return to process remaining operations
          }
          if (operation.status === "running") {
            operation.timing = completeTiming(operation.timing);
            operation.status = "completed";
          }
          if (operation.children?.length > 0) {
            this.endPendingOperations(operation.children);
          }
        } catch {
          // Continue processing other operations
        }
      }
    } catch {
      // Silently ignore
    }
  }

  private cancelOperations(operations: string[]): void {
    try {
      if (!Array.isArray(operations)) return;

      for (const op of operations) {
        try {
          const operation = this.operations.get(op);
          if (!operation) {
            this.logWarn(`Operation "${op}" not found`);
            continue; // Use continue instead of return to process remaining operations
          }
          if (operation.status === "running") {
            operation.timing = completeTiming(operation.timing);
            operation.status = "cancelled";
          }
          if (operation.children?.length > 0) {
            this.cancelOperations(operation.children);
          }
        } catch {
          // Continue processing other operations
        }
      }
    } catch {
      // Silently ignore
    }
  }

  private cleanupInteraction(
    name: string,
    interaction: TrackedInteraction
  ): void {
    try {
      const removeOps = (ops: string[]) => {
        if (!Array.isArray(ops)) return;

        for (const op of ops) {
          try {
            const operation = this.operations.get(op);
            if (!operation) {
              continue; // Use continue instead of return to process remaining operations
            }
            this.operations.delete(op);
            if (operation.children?.length > 0) {
              removeOps(operation.children);
            }
          } catch {
            // Continue processing other operations
          }
        }
      };

      if (interaction?.operations) {
        removeOps(interaction.operations);
      }

      if (name) {
        this.interactions.delete(name);
      }
    } catch {
      // Attempt basic cleanup
      try {
        if (name) this.interactions.delete(name);
      } catch {
        // Silently ignore
      }
    }
  }

  private calculateSummary(
    task: TrackedInteraction | TrackedOperation
  ): InteractionSummary {
    const defaultSummary: InteractionSummary = {
      name: task?.name ?? "unknown",
      duration: 0,
      operationCount: 0,
      successOperationCount: 0,
      failedOperationCount: 0,
      slowestOperation: undefined,
      operations: [],
      status: task?.status ?? "completed",
      metadata: task?.metadata,
    };

    try {
      let successCount = 0;
      let failedCount = 0;
      let slowest: InteractionSummary["slowestOperation"];
      let operations: InteractionSummary[] = [];
      const ops =
        "operations" in task
          ? task.operations
          : (task as TrackedOperation).children;
      const safeOps = Array.isArray(ops) ? ops : [];

      for (const op of safeOps) {
        try {
          const operation = this.operations.get(op);
          if (!operation) {
            this.logWarn(`Operation "${op}" not found`);
            continue;
          }
          const d = operation.timing?.duration ?? 0;
          if (operation.status === "completed") successCount++;
          if (operation.status === "failed") failedCount++;
          if (!slowest || d > slowest.duration) {
            slowest = {
              name: operation.name ?? "unknown",
              duration: parseFloat(d.toFixed(2)),
            };
          }
          operations.push(this.calculateSummary(operation));
        } catch {
          // Continue processing other operations
        }
      }

      return {
        name: task?.name ?? "unknown",
        duration: parseFloat((task?.timing?.duration ?? 0).toFixed(2)),
        operationCount: safeOps.length,
        successOperationCount: successCount,
        failedOperationCount: failedCount,
        slowestOperation: slowest,
        operations,
        status: task?.status ?? "completed",
        metadata: task?.metadata,
      };
    } catch {
      return defaultSummary;
    }
  }

  private flattenOperations(
    operations: InteractionSummary[]
  ): InteractionSummary[] {
    try {
      const result: InteractionSummary[] = [];
      const traverse = (ops: InteractionSummary[]) => {
        if (!Array.isArray(ops)) return;

        for (const op of ops) {
          try {
            result.push(op);
            if (op?.operations?.length > 0) {
              traverse(op.operations);
            }
          } catch {
            // Continue processing other operations
          }
        }
      };
      traverse(operations ?? []);
      return result;
    } catch {
      return [];
    }
  }

  private log(message: string, extra?: unknown): void {
    try {
      if (this.config?.enableLogging) {
        if (extra !== undefined) {
          console.log(message, extra);
        } else {
          console.log(message);
        }
      }
    } catch {
      // Silently ignore logging errors
    }
  }

  private logWarn(message: string): void {
    try {
      if (this.config?.enableLogging) {
        console.warn(message);
      }
    } catch {
      // Silently ignore logging errors
    }
  }

  private defaultBatchPusher(payload: BatchPayload): void {
    try {
      PerformanceMonitoringService?.logOperationExecutorMetrics?.(payload);
    } catch {
      // Silently ignore batch pusher errors
    }
  }
}
